import { Routes } from '@angular/router';
import { authGuard, guestGuard, paymentScheduleGuard, subUserGuard } from './guards/auth.guard';
import { adminGuard, adminGuestGuard } from './guards/admin.guard';
import {
  SEO_ABOUT,
  SEO_ACCOUNT_DELETION,
  SEO_ARTICLE,
  SEO_BLOG,
  SEO_COMING_SOON,
  SEO_CONTACT,
  SEO_DOWNLOAD,
  SEO_FAQ,
  SEO_HOME,
  SEO_LOGIN,
  SEO_NOINDEX,
  SEO_PRICING,
  SEO_PRIVACY,
  SEO_SERVICES,
  SEO_TERMS,
  SEO_THANK_YOU
} from './seo/seo-routes';

export const routes: Routes = [

  // ── Public marketing site (VEVAROK webpage) ─────────
  {
    path: '',
    loadComponent: () =>
      import('./site/site-layout.component').then(m => m.SiteLayoutComponent),
    children: [
      {
        path: '',
        pathMatch: 'full',
        canActivate: [guestGuard],
        data: { seo: SEO_HOME },
        loadComponent: () =>
          import('./site/landing/landing-page.component')
            .then(m => m.SiteLandingPageComponent)
      },
      {
        path: 'about-us',
        data: { seo: SEO_ABOUT },
        loadComponent: () =>
          import('./site/pages/about-us/about-us.component')
            .then(m => m.AboutUsComponent)
      },
      {
        path: 'services',
        data: { seo: SEO_SERVICES },
        loadComponent: () =>
          import('./site/pages/services-page/services-page.component')
            .then(m => m.ServicesPageComponent)
      },
      {
        path: 'pricing',
        data: { seo: SEO_PRICING },
        loadComponent: () =>
          import('./site/pages/pricing/pricing.component')
            .then(m => m.PricingComponent)
      },
      {
        path: 'blog',
        data: { seo: SEO_BLOG },
        loadComponent: () =>
          import('./site/pages/blog-page/blog-page.component')
            .then(m => m.BlogPageComponent)
      },
      {
        path: 'blog/:slug',
        data: { seo: SEO_ARTICLE },
        loadComponent: () =>
          import('./site/pages/blog-detail/blog-detail.component')
            .then(m => m.BlogDetailComponent)
      },
      {
        path: 'faqs',
        data: { seo: SEO_FAQ },
        loadComponent: () =>
          import('./site/pages/faq/faq.component')
            .then(m => m.FaqComponent)
      },
      {
        path: 'support',
        redirectTo: 'about-us',
        pathMatch: 'full'
      },
      {
        path: 'contact-us',
        data: { seo: SEO_CONTACT },
        loadComponent: () =>
          import('./site/pages/contact-us/contact-us.component')
            .then(m => m.ContactUsComponent)
      },
      {
        path: 'download-app',
        data: { seo: SEO_DOWNLOAD },
        loadComponent: () =>
          import('./site/pages/download-app/download-app.component')
            .then(m => m.DownloadAppComponent)
      },
      {
        path: 'privacy-policy',
        data: { seo: SEO_PRIVACY },
        loadComponent: () =>
          import('./site/pages/privacy/privacy.component')
            .then(m => m.PrivacyComponent)
      },
      {
        path: 'terms',
        data: { seo: SEO_TERMS },
        loadComponent: () =>
          import('./site/pages/terms/terms.component')
            .then(m => m.TermsComponent)
      },
      {
        path: 'account-deletion',
        data: { seo: SEO_ACCOUNT_DELETION },
        loadComponent: () =>
          import('./site/pages/account-deletion/account-deletion.component')
            .then(m => m.AccountDeletionComponent)
      },
      {
        path: 'coming-soon',
        data: { seo: SEO_COMING_SOON },
        loadComponent: () =>
          import('./site/pages/coming-soon/coming-soon.component')
            .then(m => m.ComingSoonComponent)
      },
      {
        path: 'thank-you',
        data: { seo: SEO_THANK_YOU },
        loadComponent: () =>
          import('./site/pages/thank-you/thank-you.component')
            .then(m => m.ThankYouComponent)
      }
    ]
  },

  // login (lazy)
  {
    path: 'login',
    data: { seo: SEO_LOGIN },
    loadComponent: () =>
      import('./pages/login/login-page.component')
        .then(m => m.LoginPageComponent),
    canActivate: [guestGuard]
  },

  // ── Admin Panel (fully isolated app) ───────────────
  {
    path: 'admin/login',
    data: { seo: { ...SEO_NOINDEX, title: 'Admin Login | VEVAROK' } },
    canActivate: [adminGuestGuard],
    loadComponent: () =>
      import('./pages/admin/admin-login-page.component')
        .then(m => m.AdminLoginPageComponent)
  },
  {
    path: 'admin',
    data: { seo: { ...SEO_NOINDEX, title: 'Admin | VEVAROK' } },
    canActivate: [adminGuard],
    loadComponent: () =>
      import('./layouts/admin-layout/admin-layout.component')
        .then(m => m.AdminLayoutComponent),
    children: [
      { path: '', pathMatch: 'full', redirectTo: 'dashboard' },
      {
        path: 'dashboard',
        data: { seo: { ...SEO_NOINDEX, title: 'Admin Dashboard | VEVAROK' } },
        loadComponent: () =>
          import('./pages/admin/admin-dashboard-page.component')
            .then(m => m.AdminDashboardPageComponent)
      },
      {
        path: 'users',
        data: { seo: { ...SEO_NOINDEX, title: 'Users | VEVAROK Admin' } },
        loadComponent: () =>
          import('./pages/admin/admin-user-list-page.component')
            .then(m => m.AdminUserListPageComponent)
      },
      {
        path: 'inquiries',
        data: { seo: { ...SEO_NOINDEX, title: 'Inquiries | VEVAROK Admin' } },
        loadComponent: () =>
          import('./pages/admin/admin-inquiry-list-page.component')
            .then(m => m.AdminInquiryListPageComponent)
      },
      {
        path: 'invoices',
        data: { seo: { ...SEO_NOINDEX, title: 'Invoice History | VEVAROK Admin' } },
        loadComponent: () =>
          import('./pages/admin/admin-invoice-history-page.component')
            .then(m => m.AdminInvoiceHistoryPageComponent)
      },
      {
        path: 'blogs/create',
        data: { seo: { ...SEO_NOINDEX, title: 'Create Blog | VEVAROK Admin' } },
        loadComponent: () =>
          import('./pages/admin/admin-blog-form-page.component')
            .then(m => m.AdminBlogFormPageComponent)
      },
      {
        path: 'blogs/edit/:id',
        data: { seo: { ...SEO_NOINDEX, title: 'Edit Blog | VEVAROK Admin' } },
        loadComponent: () =>
          import('./pages/admin/admin-blog-form-page.component')
            .then(m => m.AdminBlogFormPageComponent)
      },
      {
        path: 'blogs',
        data: { seo: { ...SEO_NOINDEX, title: 'Blogs | VEVAROK Admin' } },
        loadComponent: () =>
          import('./pages/admin/admin-blog-list-page.component')
            .then(m => m.AdminBlogListPageComponent)
      },
      {
        path: 'templates/create',
        data: { seo: { ...SEO_NOINDEX, title: 'Create Template | VEVAROK Admin' } },
        loadComponent: () =>
          import('./pages/admin/admin-template-form-page.component')
            .then(m => m.AdminTemplateFormPageComponent)
      },
      {
        path: 'templates/edit/:id',
        data: { seo: { ...SEO_NOINDEX, title: 'Edit Template | VEVAROK Admin' } },
        loadComponent: () =>
          import('./pages/admin/admin-template-form-page.component')
            .then(m => m.AdminTemplateFormPageComponent)
      },
      {
        path: 'templates',
        data: { seo: { ...SEO_NOINDEX, title: 'Templates | VEVAROK Admin' } },
        loadComponent: () =>
          import('./pages/admin/admin-template-list-page.component')
            .then(m => m.AdminTemplateListPageComponent)
      },
      {
        path: 'modules',
        data: { seo: { ...SEO_NOINDEX, title: 'Modules | VEVAROK Admin' } },
        loadComponent: () =>
          import('./pages/admin/admin-module-list-page.component')
            .then(m => m.AdminModuleListPageComponent)
      },
      {
        path: 'settings',
        data: { seo: { ...SEO_NOINDEX, title: 'Settings | VEVAROK Admin' } },
        loadComponent: () =>
          import('./pages/admin/admin-settings-groups-page.component')
            .then(m => m.AdminSettingsGroupsPageComponent)
      },
      {
        path: 'settings/:group',
        data: { seo: { ...SEO_NOINDEX, title: 'Settings | VEVAROK Admin' } },
        loadComponent: () =>
          import('./pages/admin/admin-settings-list-page.component')
            .then(m => m.AdminSettingsListPageComponent)
      },
      {
        path: 'documentation/:id',
        data: { seo: { ...SEO_NOINDEX, title: 'Documentation | VEVAROK Admin' } },
        loadComponent: () =>
          import('./pages/admin/admin-docs-viewer-page.component')
            .then(m => m.AdminDocsViewerPageComponent)
      },
      {
        path: 'documentation',
        data: { seo: { ...SEO_NOINDEX, title: 'Documentation | VEVAROK Admin' } },
        loadComponent: () =>
          import('./pages/admin/admin-docs-list-page.component')
            .then(m => m.AdminDocsListPageComponent)
      },
      {
        path: 'masters/:key',
        data: { seo: { ...SEO_NOINDEX, title: 'Masters | VEVAROK Admin' } },
        loadComponent: () =>
          import('./pages/admin/admin-master-page.component')
            .then(m => m.AdminMasterPageComponent)
      },
      { path: '**', redirectTo: 'dashboard' }
    ]
  },

  // main layout (protected user app)
  {
    path: '',
    canActivate: [authGuard],
    data: { seo: SEO_NOINDEX },
    loadComponent: () =>
      import('./layouts/main-layout/main-layout.component')
        .then(m => m.MainLayoutComponent),

    children: [

      {
        path: 'dashboard',
        data: { seo: { ...SEO_NOINDEX, title: 'Dashboard | VEVAROK' } },
        loadComponent: () =>
          import('./pages/dashboard/dashboard-page.component')
            .then(m => m.DashboardPageComponent)
      },

      { path: 'ledger', redirectTo: 'customer', pathMatch: 'full' },

      {
        path: 'customer',
        data: { seo: { ...SEO_NOINDEX, title: 'Ledger | VEVAROK' } },
        loadComponent: () =>
          import('./pages/ledger/ledger-page.component')
            .then(m => m.LedgerPageComponent)
      },

      {
        path: 'recycle-bin',
        data: { seo: { ...SEO_NOINDEX, title: 'Recycle Bin | VEVAROK' } },
        loadComponent: () =>
          import('./pages/recycle-bin/recycle-bin-page.component')
            .then(m => m.RecycleBinPageComponent)
      },

      {
        path: 'follow-up',
        data: { seo: { ...SEO_NOINDEX, title: 'Follow Up | VEVAROK' } },
        loadComponent: () =>
          import('./pages/followup-list/followup-list-page.component')
            .then(m => m.FollowupListPageComponent)
      },

      {
        path: 'recurring-follow-up',
        canActivate: [paymentScheduleGuard],
        data: { seo: { ...SEO_NOINDEX, title: 'Recurring Follow Up | VEVAROK' } },
        loadComponent: () =>
          import('./pages/recurring-followup-list/recurring-followup-list-page.component')
            .then(m => m.RecurringFollowupListPageComponent)
      },

      {
        path: 'pending-transactions',
        data: { seo: { ...SEO_NOINDEX, title: 'Pending Transactions | VEVAROK' } },
        loadComponent: () =>
          import('./pages/pending-transactions/pending-transactions-page.component')
            .then(m => m.PendingTransactionsPageComponent)
      },

      {
        path: 'customer-statement',
        data: { seo: { ...SEO_NOINDEX, title: 'Customer Statement | VEVAROK' } },
        loadComponent: () =>
          import('./pages/customer-statement/customer-statement-page.component')
            .then(m => m.CustomerStatementPageComponent)
      },

      {
        path: 'common-statement',
        data: { seo: { ...SEO_NOINDEX, title: 'Statement | VEVAROK' } },
        loadComponent: () =>
          import('./pages/common-statement/common-statement-page.component')
            .then(m => m.CommonStatementPageComponent)
      },

      {
        path: 'profile',
        data: { seo: { ...SEO_NOINDEX, title: 'Profile | VEVAROK' } },
        loadComponent: () =>
          import('./pages/user-profile/user-profile-page.component')
            .then(m => m.UserProfilePageComponent)
      },

      {
        path: 'settings',
        data: { seo: { ...SEO_NOINDEX, title: 'Settings | VEVAROK' } },
        loadComponent: () =>
          import('./pages/settings/settings-page.component')
            .then(m => m.SettingsPageComponent)
      },

      {
        path: 'plan',
        data: { seo: { ...SEO_NOINDEX, title: 'Plan | VEVAROK' } },
        loadComponent: () =>
          import('./pages/plan/plan-page.component')
            .then(m => m.PlanPageComponent)
      },

      {
        path: 'renewal-history',
        data: { seo: { ...SEO_NOINDEX, title: 'Renewal History | VEVAROK' } },
        loadComponent: () =>
          import('./pages/renewal-history/renewal-history-page.component')
            .then(m => m.RenewalHistoryPageComponent)
      },

      {
        path: 'add-ons',
        data: { seo: { ...SEO_NOINDEX, title: 'Add-ons | VEVAROK' } },
        loadComponent: () =>
          import('./pages/add-ons/add-ons-page.component')
            .then(m => m.AddOnsPageComponent)
      },

      {
        path: 'sub-users',
        canActivate: [subUserGuard],
        data: { seo: { ...SEO_NOINDEX, title: 'Sub Users | VEVAROK' } },
        loadComponent: () =>
          import('./pages/sub-users/sub-users-page.component')
            .then(m => m.SubUsersPageComponent)
      },

      {
        path: 'payment/success',
        data: { seo: { ...SEO_NOINDEX, title: 'Payment successful | VEVAROK' }, paymentResult: 'success' },
        loadComponent: () =>
          import('./pages/payment-result/payment-result-page.component')
            .then(m => m.PaymentResultPageComponent)
      },

      {
        path: 'payment/failure',
        data: { seo: { ...SEO_NOINDEX, title: 'Payment failed | VEVAROK' }, paymentResult: 'failure' },
        loadComponent: () =>
          import('./pages/payment-result/payment-result-page.component')
            .then(m => m.PaymentResultPageComponent)
      },

      {
        path: 'version-history',
        data: { seo: { ...SEO_NOINDEX, title: 'Version History | VEVAROK' } },
        loadComponent: () =>
          import('./pages/version-list/version-list-page.component')
            .then(m => m.VersionListPageComponent)
      },

      {
        path: 'faq',
        data: { seo: { ...SEO_NOINDEX, title: 'Help | VEVAROK' } },
        loadComponent: () =>
          import('./pages/faq-list/faq-list-page.component')
            .then(m => m.FaqListPageComponent)
      },

      {
        path: 'ai-chat',
        data: { seo: { ...SEO_NOINDEX, title: 'AI Chat | VEVAROK' } },
        loadComponent: () =>
          import('./pages/app-chat/app-chat.component')
            .then(m => m.AppChatComponent)
      },

      { path: '**', redirectTo: 'dashboard' }
    ]
  }
];
